import { useState, useRef, useEffect } from "react";
import { compressImage } from "@/lib/compressImage";
import { X, Save, Loader2, Camera, ImagePlus } from "lucide-react";
import api from "@/lib/api";
import { todayISO } from "@/lib/utils";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n.jsx";

const MEASUREMENT_FIELDS = [
  { k: "chest_cm", lAr: "الصدر", lEn: "Chest" },
  { k: "waist_cm", lAr: "الخصر", lEn: "Waist" },
  { k: "hips_cm",  lAr: "الأرداف", lEn: "Hips" },
  { k: "arm_cm",   lAr: "الذراع", lEn: "Arm" },
  { k: "thigh_cm", lAr: "الفخذ", lEn: "Thigh" },
  { k: "neck_cm",  lAr: "الرقبة", lEn: "Neck" },
];

export default function WeightLogModal({ onClose, currentWeight, onSaved }) {
  const { lang } = useI18n();
  // ── iOS "black screen bounce" safety net ─────────────────────────────
  // On iOS Safari the camera intent can force-remount the WebView while the
  // user is on the modal (see compressImage below). To avoid the user losing
  // their typed weight/date/notes/measurements, we persist those fields to
  // sessionStorage on every change and hydrate from it on mount. Photos are
  // not persisted (File objects aren't serialisable), but the compression
  // fix keeps the tab alive in most cases now.
  const DRAFT_KEY = "calofay:weight-log-draft";
  const draft = (() => {
    try { return JSON.parse(sessionStorage.getItem(DRAFT_KEY) || "null") || {}; } catch { return {}; }
  })();

  // Wrap the parent's onClose so an explicit user dismiss (X, backdrop tap,
  // ESC) also clears the draft. That way when they navigate to Progress
  // later, the modal does NOT surprise them by popping open again. The
  // iOS purge scenario never reaches this handler — the WebView is killed
  // before it can fire — so the draft is preserved for the reopen recovery.
  const handleClose = () => {
    try { sessionStorage.removeItem(DRAFT_KEY); } catch {}
    onClose?.();
  };

  const [weight, setWeight] = useState(draft.weight || currentWeight || "");
  // `todayISO()` snapshot at mount so clamp logic stays consistent
  // through the modal's lifetime — using `todayISO()` inline elsewhere
  // meant a fresh Date() every render, which briefly allowed a user
  // to type tomorrow's date via keyboard on iOS while the input's
  // `max` was still yesterday's.
  const TODAY = todayISO();
  const clampDate = (v) => (v && v > TODAY ? TODAY : v || TODAY);
  const [date, setDate] = useState(clampDate(draft.date || TODAY));
  const [notes, setNotes] = useState(draft.notes || "");
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [progressText, setProgressText] = useState("");
  const [showMeasurements, setShowMeasurements] = useState(Object.keys(draft.measurements || {}).length > 0);
  const [measurements, setMeasurements] = useState(draft.measurements || {});
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);

  // Persist textual fields to sessionStorage on every change. Stamped with
  // `savedAt` so Progress can decide whether an outstanding draft is fresh
  // enough to warrant auto-reopening the modal (iOS camera purge recovery)
  // or stale (user closed the modal manually and came back later — in that
  // case we do NOT want to pop the modal open again on navigation).
  useEffect(() => {
    try {
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ weight, date, notes, measurements, savedAt: Date.now() }));
    } catch {}
  }, [weight, date, notes, measurements]);

  const [preparing, setPreparing] = useState(false);

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (photos.length + files.length > 8) {
      toast.error(lang === "en" ? "Maximum 8 photos at a time" : "الحد الأقصى 8 صور في المرة الواحدة");
      return;
    }
    setPreparing(true);
    try {
      const next = [];
      for (const f of files) {
        if (!f.type.startsWith("image/") && !/\.(heic|heif)$/i.test(f.name)) { toast.error(lang === "en" ? "Files must be images only" : "يجب أن تكون الملفات صور فقط"); continue; }
        if (f.size > 30 * 1024 * 1024) { toast.error(lang === "en" ? `Image exceeds 30MB max: ${f.name}` : `تخطت الصورة الحد الأقصى (30MB): ${f.name}`); continue; }
        // Try compressing; if it fails (e.g. HEIC on non-Safari), fall back
        // to the raw file so the user still sees something and the upload
        // path continues (backend accepts HEIC/JPEG).
        let final = f;
        try { final = await compressImage(f); } catch { final = f; }
        next.push({ file: final, preview: URL.createObjectURL(final) });
      }
      if (next.length) setPhotos((prev) => [...prev, ...next]);
    } finally {
      setPreparing(false);
    }
  };

  const removePhoto = (idx) => {
    setPhotos((prev) => {
      const copy = [...prev];
      const [rm] = copy.splice(idx, 1);
      if (rm?.preview) URL.revokeObjectURL(rm.preview);
      return copy;
    });
  };

  const save = async () => {
    const w = Number(weight);
    if (!w || w < 20 || w > 500) return toast.error(lang === "en" ? "Enter a valid weight" : "أدخل وزن صحيح");
    // Belt-and-suspenders: even if the input `max` was bypassed
    // (iOS keyboard, paste, DevTools), refuse future-dated entries
    // outright. Matches the backend `not_future_date` validator so
    // the UI errors are consistent.
    if (date > TODAY) return toast.error(lang === "en" ? "Date can't be in the future" : "لا يمكن اختيار تاريخ في المستقبل");
    setSaving(true);
    const cleanMeasurements = Object.fromEntries(
      Object.entries(measurements).filter(([, v]) => v !== "" && !isNaN(Number(v))).map(([k, v]) => [k, Number(v)])
    );
    const hasMeasurements = Object.keys(cleanMeasurements).length > 0;
    try {
      setProgressText(lang === "en" ? "Saving weight…" : "جاري حفظ الوزن…");
      const { data: weightResp } = await api.post("/weight", { date, weight_kg: w, notes: notes || null });

      let uploaded = 0;
      const measurementsPayload = hasMeasurements ? JSON.stringify(cleanMeasurements) : null;
      for (const p of photos) {
        uploaded += 1;
        setProgressText(lang === "en" ? `Uploading photo ${uploaded} of ${photos.length}…` : `جاري رفع الصورة ${uploaded} من ${photos.length}…`);
        const fd = new FormData();
        fd.append("file", p.file);
        fd.append("date", date);
        fd.append("weight_kg", String(w));
        if (notes) fd.append("notes", notes);
        fd.append("angle", "front");
        if (measurementsPayload) fd.append("measurements", measurementsPayload);
        // Let axios/browser auto-set the multipart Content-Type WITH
        // the boundary parameter. Manually setting
        // `Content-Type: multipart/form-data` without a boundary
        // causes iOS WKWebView to send malformed form data — the
        // server rejects with 422/400 which was previously bubbling
        // up as a confusing "Not authenticated" toast on our side.
        await api.post("/progress-photos", fd);
      }

      setProgressText(lang === "en" ? "Saved ✅" : "تم الحفظ ✅");
      // Clear the sessionStorage draft — a successful save means the draft
      // no longer needs to survive an iOS WebView purge.
      try { sessionStorage.removeItem(DRAFT_KEY); } catch {}
      onSaved({ weight: w, ...weightResp });
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === "en" ? "Save failed" : "تعذر الحفظ"));
    } finally {
      setSaving(false);
      setProgressText("");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" onClick={handleClose} dir={lang === "en" ? "ltr" : "rtl"}>
      <div
        onClick={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
        className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl overflow-hidden max-h-[92vh] flex flex-col shadow-2xl"
        style={{ touchAction: "pan-y", overscrollBehavior: "contain" }}
        data-testid="weight-log-modal"
      >
        {/* Header — centered title, dismiss control on the trailing side */}
        <div className="px-5 pt-5 pb-3 flex-shrink-0 relative">
          <div className="flex items-center justify-center">
            <div className="text-base font-black text-neutral-900">{lang === "en" ? "Update Weight" : "تحديث الوزن"}</div>
          </div>
          <button
            onClick={handleClose}
            className="absolute top-4 left-4 w-8 h-8 rounded-full bg-neutral-100 hover:bg-neutral-200 flex items-center justify-center transition-colors"
            data-testid="weight-modal-close"
            aria-label={lang === "en" ? "Close" : "إغلاق"}
          >
            <X className="w-4 h-4" />
          </button>
          {/* iOS-style drag pill on mobile */}
          <div className="sm:hidden absolute top-1.5 left-1/2 -translate-x-1/2 w-10 h-1 bg-neutral-200 rounded-full" />
        </div>
        <div className="px-5 pb-5 space-y-4 overflow-y-auto overflow-x-hidden" style={{ overscrollBehavior: "contain" }}>
          {/* Hero weight input */}
          <div className="bg-gradient-to-br from-brand-50 to-white rounded-3xl p-5 border border-brand-100">
            <div className="text-[11px] text-brand-600 font-bold uppercase tracking-wider text-center mb-2">{lang === "en" ? "Your weight today" : "وزنك اليوم"}</div>
            <div className="flex items-baseline justify-center gap-1">
              <input
                type="number" step="0.1" min="20" max="500" inputMode="decimal"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                placeholder="--"
                className="w-24 bg-transparent text-center text-5xl font-black num focus:outline-none placeholder:text-neutral-300"
                data-testid="weight-input"
              />
              <span className="text-lg font-bold text-neutral-500">{lang === "en" ? "kg" : "كجم"}</span>
            </div>
          </div>

          {/* Date row */}
          <div>
            <div className="text-xs text-neutral-500 mb-1.5 text-right font-semibold">{lang === "en" ? "Date" : "التاريخ"}</div>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(clampDate(e.target.value))}
              max={TODAY}
              className="w-full bg-neutral-50 rounded-2xl px-4 py-3 text-right border border-neutral-200 focus:border-brand-500 focus:bg-white focus:outline-none transition-colors"
              data-testid="weight-date"
            />
          </div>

          {/* Multi-photo picker: camera or gallery (works on iOS + Android) */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[10px] text-neutral-400">{photos.length}/8</div>
              <div className="text-xs text-neutral-500 font-semibold">{lang === "en" ? "Photos (optional)" : "صور (اختياري)"}</div>
            </div>
            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
              className="hidden"
              data-testid="weight-photo-camera-input"
            />
            <input
              ref={galleryRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/*"
              multiple
              onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
              className="hidden"
              data-testid="weight-photo-gallery-input"
            />

            {preparing && (
              <div className="flex items-center gap-2 mb-2 text-xs text-neutral-500 bg-neutral-50 rounded-xl px-3 py-2" data-testid="weight-photo-preparing">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>{lang === "en" ? "Processing photo…" : "جاري معالجة الصورة…"}</span>
              </div>
            )}

            {photos.length > 0 && (
              <div className="grid grid-cols-4 gap-2 mb-2" data-testid="weight-photo-previews">
                {photos.map((p, idx) => (
                  <div key={idx} className="relative rounded-xl overflow-hidden aspect-square bg-neutral-100">
                    <img src={p.preview} alt="" className="w-full h-full object-cover" />
                    <button
                      onClick={() => removePhoto(idx)}
                      className="absolute top-1 left-1 w-6 h-6 bg-black/60 text-white rounded-full flex items-center justify-center hover:bg-black/80"
                      data-testid={`weight-photo-remove-${idx}`}
                      aria-label={lang === "en" ? "Delete photo" : "حذف الصورة"}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => cameraRef.current?.click()}
                className="flex flex-col items-center justify-center gap-1 py-3 rounded-2xl border-2 border-dashed border-neutral-200 hover:border-brand-500 hover:bg-brand-50/40 text-neutral-600 hover:text-brand-500 transition"
                data-testid="weight-photo-camera-btn"
              >
                <Camera className="w-5 h-5" />
                <span className="text-xs font-semibold">{lang === "en" ? "Take photo" : "التقاط صورة"}</span>
              </button>
              <button
                type="button"
                onClick={() => galleryRef.current?.click()}
                className="flex flex-col items-center justify-center gap-1 py-3 rounded-2xl border-2 border-dashed border-neutral-200 hover:border-brand-500 hover:bg-brand-50/40 text-neutral-600 hover:text-brand-500 transition"
                data-testid="weight-photo-gallery-btn"
              >
                <ImagePlus className="w-5 h-5" />
                <span className="text-xs font-semibold">{lang === "en" ? "From gallery" : "من الاستوديو"}</span>
              </button>
            </div>
            <div className="text-[10px] text-neutral-400 mt-1.5 text-center">{lang === "en" ? "Photos saved securely · up to 8 photos" : "تحفظ الصور بأمان · حتى 8 صور"}</div>
          </div>

          {/* Measurements toggle — collapsible */}
          <div>
            <button
              type="button"
              onClick={() => setShowMeasurements((v) => !v)}
              className="w-full bg-white shadow-card rounded-2xl px-4 py-3 flex items-center justify-between text-sm"
              data-testid="weight-measurements-toggle"
            >
              <span className="text-brand-500 font-semibold">{showMeasurements ? (lang === "en" ? "Hide" : "إخفاء") : (lang === "en" ? "Show" : "إظهار")}</span>
              <span className="flex items-center gap-2 font-semibold text-neutral-700">
                <span className="text-xs text-neutral-400">{lang === "en" ? "(optional)" : "(اختياري)"}</span> {lang === "en" ? "Add measurements" : "إضافة القياسات"}
              </span>
            </button>
            {showMeasurements && (
              <div className="grid grid-cols-2 gap-2 mt-2" data-testid="weight-measurements-grid">
                {MEASUREMENT_FIELDS.map((m) => (
                  <label key={m.k} className="bg-white shadow-card rounded-2xl px-3 py-2">
                    <div className="text-[10px] text-neutral-500 text-right mb-1">{lang === "en" ? m.lEn : m.lAr} {lang === "en" ? "(cm)" : "(سم)"}</div>
                    <input
                      type="number"
                      step="0.1"
                      value={measurements[m.k] || ""}
                      onChange={(e) => setMeasurements({ ...measurements, [m.k]: e.target.value })}
                      className="w-full bg-white shadow-card rounded-xl px-2 py-1.5 text-sm text-right num focus:outline-none"
                      data-testid={`weight-m-${m.k}`}
                    />
                  </label>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="text-xs text-neutral-500 mb-1.5 text-right font-semibold">{lang === "en" ? "Notes (optional)" : "ملاحظات (اختياري)"}</div>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
              placeholder={lang === "en" ? "e.g. Feeling more energetic, started daily walking" : "مثال: شعور بنشاط أعلى، بدأت المشي اليومي"}
              className="w-full bg-white shadow-card rounded-2xl px-4 py-3 text-right focus:outline-none"
              data-testid="weight-notes" />
          </div>
        </div>

        {/* Sticky footer with progress indicator */}
        <div className="p-4 border-t border-neutral-100 flex-shrink-0">
          {saving && progressText && (
            <div className="mb-2 text-center text-xs text-brand-500 font-semibold flex items-center justify-center gap-1.5" data-testid="weight-upload-progress">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              <span>{progressText}</span>
            </div>
          )}
          <button onClick={save} disabled={saving}
            className="w-full orange-gradient text-white font-bold py-3.5 rounded-full flex items-center justify-center gap-1 disabled:opacity-60"
            data-testid="weight-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> {lang === "en" ? "Save" : "حفظ"}</>}
          </button>
        </div>
      </div>
    </div>
  );
}
